import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  FormControl,
  FormControlLabel,
  Checkbox,
  Fade,
  LinearProgress,
} from '@mui/material';
import {
  Flag as GoalIcon,
  Warning as ChallengeIcon,
  Inventory as ResourceIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import default_theme from 'assets/themes/themes';

const WizardForm = ({ onFormSubmit, onCancel, initialData = null, isEditing = false }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(
    initialData || {
      goals: '',
      challenges: '',
      resources: '',
      description: '',
      relatedFields: []
    }
  );

  // Update form data when initialData changes (for editing)
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      // Reset to first step when editing
      if (isEditing) {
        setActiveStep(0);
      }
    }
  }, [initialData, isEditing]);


  const availableFields = [
    'Climate Change Adaptation',
    'Flood Management',
    'Risk Assessment & Analysis',
    'Building Regulations & Land Use',
    'Civil Protection',
    'Community Resilience',
    'Crisis Coordination & Response',
    'Cultural Heritage Protection',
    'Disaster Risk Management',
    'Early Warning Systems',
    'Emergency Communication',
    'Emergency Planning',
    'Environmental Protection',
    'Healthcare & Wellbeing',
    'Public Safety & Preparedness',
    'Recovery & Rebuilding',
    'Socio-economic Resilience',
    'Training & Public Awareness',
    'Volunteer & Civil Society Engagement',
    'Wildfire Prevention & Management'
  ];

  const steps = [
    {
      label: 'Goals to Achieve',
      description: 'What are you trying to accomplish?',
      icon: <GoalIcon />,
      field: 'goals'
    },
    {
      label: 'Challenges Faced',
      description: 'What obstacles are you encountering?',
      icon: <ChallengeIcon />,
      field: 'challenges'
    },
    {
      label: 'Available Resources',
      description: 'What resources do you have access to?',
      icon: <ResourceIcon />,
      field: 'resources'
    },
    {
      label: 'Project Description',
      description: 'Brief overview of your project or initiative',
      icon: <DescriptionIcon />,
      field: 'description'
    },
    {
      label: 'Related Fields',
      description: 'Select relevant areas and indicators for your project',
      icon: <CategoryIcon />,
      field: 'relatedFields'
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = () => {
    onFormSubmit(formData);
  };

  const isStepValid = (stepIndex) => {
    const step = steps[stepIndex];
    if (step.field === 'relatedFields') {
      return formData[step.field].length > 0;
    }
    return formData[step.field].trim().length > 0;
  };

  const isFormComplete = () => {
    return steps.every((_, index) => isStepValid(index));
  };

  const handleFieldToggle = (fieldValue) => {
    setFormData(prev => {
      const currentFields = prev.relatedFields;
      const newFields = currentFields.includes(fieldValue)
        ? currentFields.filter(f => f !== fieldValue)
        : [...currentFields, fieldValue];
      return {
        ...prev,
        relatedFields: newFields
      };
    });
  };

  const renderStepContent = (step, stepIndex) => {
    if (step.field === 'relatedFields') {
      // Split fields into columns (5 per column)
      const itemsPerColumn = 5;
      const columns = [];
      for (let i = 0; i < availableFields.length; i += itemsPerColumn) {
        columns.push(availableFields.slice(i, i + itemsPerColumn));
      }

      return (
        <FormControl fullWidth sx={{ mt: 2 }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 3,
            flexWrap: 'wrap',
            p: 1,
            bgcolor: '#FFF',
          }}>
            {columns.map((columnFields, columnIndex) => (
              <Box
                key={columnIndex}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  flex: '1 1 0',
                  minWidth: '200px'
                }}
              >
                {columnFields.map((field) => (
                  <FormControlLabel
                    key={field}
                    control={
                      <Checkbox
                        checked={formData.relatedFields.includes(field)}
                        onChange={() => handleFieldToggle(field)}
                        sx={{
                          bgcolor: '#FFF',
                          color: '#00b894',
                          '&.Mui-checked': {
                            color: '#00b894',
                          },
                        }}
                      />
                    }
                    label={field}
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.95rem'
                      }
                    }}
                  />
                ))}
              </Box>
            ))}
          </Box>
          {formData.relatedFields.length > 0 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Selected ({formData.relatedFields.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {formData.relatedFields.map((value) => (
                  <Chip
                    key={value}
                    label={value}
                    size="small"
                    sx={{
                      bgcolor: '#e8f5e8',
                      color: '#00b894',
                      '& .MuiChip-deleteIcon': { color: '#00b894' }
                    }}
                    onDelete={() => handleFieldToggle(value)}
                  />
                ))}
              </Box>
            </Box>
          )}
        </FormControl>
      );
    }

    return (
      <TextField
        fullWidth
        multiline
        minRows={3}
        maxRows={6}
        value={formData[step.field]}
        onChange={(e) => handleInputChange(step.field, e.target.value)}
        placeholder={`Describe your ${step.label.toLowerCase()}...`}
        variant="outlined"
        sx={{
          mt: 2,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: '#00a085',          // normal
            },
            '&:hover fieldset': {
              borderColor: '#00a085',          // hover
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00b894',          // focus (user typing)
            },
          },
        }}
      />
    );
  };

  return (
    <Fade in timeout={500}>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          p: 3,
          overflow: 'hidden',
          bgcolor: default_theme.palette.primary.bgcolor,
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#00b894', mb: 1 }}>
            {isEditing ? 'Edit Project Information' : 'Smart Recommendations Wizard'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isEditing
              ? 'Update your project information below'
              : 'Help us understand your project to provide tailored solutions.'}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(activeStep / (steps.length - 1)) * 100}
            sx={{
              mt: 2,
              height: 6,
              borderRadius: 3,
              bgcolor: '#f0f0f0',
              '& .MuiLinearProgress-bar': {
                bgcolor: '#00b894',
                borderRadius: 3
              }
            }}
          />
        </Box>

        {/* Form Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  icon={
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: index <= activeStep ? '#00b894' : '#e0e0e0',
                        color: index <= activeStep ? 'white' : '#666',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {step.icon}
                    </Box>
                  }
                >
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {step.description}
                  </Typography>

                  {/* formfield label */}
                  <Paper
                    elevation={1}
                    sx={{
                      p: 3,
                      bgcolor: '#FFF',
                      border: '1px solid #e0e0e0',
                      borderRadius: 2
                    }}
                  >
                    {renderStepContent(step, index)}
                  </Paper>

                  <Box sx={{ mb: 2, mt: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        onClick={index === steps.length - 1 ? handleSubmit : handleNext}
                        disabled={!isStepValid(index)}
                        sx={{
                          bgcolor: '#00b894',
                          '&:hover': { bgcolor: '#00a085' },
                          '&:disabled': { bgcolor: '#e0e0e0' }
                        }}
                      >
                        {index === steps.length - 1 ? (isEditing ? 'Update' : 'Start Wizard') : 'Continue'}
                      </Button>

                      {index > 0 && (
                        <Button onClick={handleBack} sx={{ color: '#666' }}>
                          Back
                        </Button>
                      )}

                      <Button
                        onClick={onCancel}
                        sx={{ color: '#666', ml: 'auto', bgcolor: default_theme.palette.secondary.bgcolor }}
                      >
                        Skip for Now
                      </Button>
                    </Box>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {/* Summary when complete */}
          {isFormComplete() && activeStep === steps.length && (
            <Paper
              elevation={2}
              sx={{
                p: 3,
                mt: 2,
                bgcolor: '#f8f9fa',
                border: '2px solid #00b894',
                borderRadius: 2
              }}
            >
              <Typography variant="h6" sx={{ color: '#00b894', mb: 2 }}>
                {isEditing ? 'Ready to Update Information' : 'Ready to Generate Recommendations'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Based on your inputs, we'll analyze our solution catalog and provide personalized recommendations for your project.
              </Typography>

              <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    bgcolor: '#00b894',
                    '&:hover': { bgcolor: '#00a085' },
                    px: 4
                  }}
                >
                  {isEditing ? 'Update Information' : 'Generate Recommendations'}
                </Button>
                <Button
                  onClick={() => setActiveStep(steps.length - 1)}
                  sx={{ color: '#666' }}
                >
                  Review Inputs
                </Button>
              </Box>
            </Paper>
          )}
        </Box>
      </Box>
    </Fade>
  );
};

export default WizardForm;